<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu 
      :user="user" 
      :isMenuOpen="isMenuOpen" 
      @toggle-menu="toggleMenu" 
      @close-menu="closeMenu" 
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <div class="flex items-center justify-between mb-6">
            <h1 class="text-xl font-bold">Detalles del Prospecto</h1>
            <button 
              @click="goBack" 
              class="hidden inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Volver
            </button>
          </div>
          
          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
          
          <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ errorMessage }}
          </div>

          <div v-if="prospect" class="space-y-6">
            <!-- Información Personal -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6">
              <h2 class="text-lg font-semibold mb-4 flex items-center">
                Información Personal
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <p class="text-sm font-medium text-gray-500">Nombre Completo</p>
                  <p class="text-sm uppercase font-medium">{{ prospect.ProspectName }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Sexo</p>
                  <p class="text-sm">{{ prospect.Sex === 'M' ? 'Masculino' : 'Femenino' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Fecha de Nacimiento</p>
                  <p class="text-sm">{{ formatDate(prospect.Birthdate) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">CURP</p>
                  <p class="text-sm">{{ prospect.Curp || 'No especificado' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Estatus</p>
                  <p class="text-sm">{{ prospect.Status }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Fecha de Expiración</p>
                  <p class="text-sm">{{ prospect.ExpirationDate }}</p>
                </div>
              </div>
            </div>

            <!-- Contacto -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6">
              <h2 class="text-lg font-semibold mb-4 flex items-center">
                Información de Contacto
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p class="text-sm font-medium text-gray-500">Dirección</p>
                  <p class="text-sm">{{ prospect.Address || 'No especificada' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Correo Electrónico</p>
                  <p class="text-sm">{{ prospect.Email || 'No especificado' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Teléfono</p>
                  <p class="text-sm">{{ prospect.Phone || 'No especificado' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Celular</p>
                  <p class="text-sm">{{ prospect.Mobile || 'No especificado' }}</p>
                </div>
              </div>
            </div>

            <!-- Intereses Académicos -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6">
              <h2 class="text-lg font-semibold mb-4 flex items-center">
                Intereses Académicos
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p class="text-sm font-medium text-gray-500">Carrera</p>
                  <p class="text-sm">{{ prospect.Career || 'No especificada' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Periodo</p>
                  <p class="text-sm">{{ prospect.PeriodId || 'No especificado' }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Turno</p>
                  <p class="text-sm">{{ getTurnoName(prospect.ShiftId) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Escuela de Procedencia</p>
                  <p class="text-sm">{{ prospect.SchoolId ? `ID: ${prospect.SchoolId}` : 'No especificada' }}</p>
                </div>
              </div>
            </div>

            <!-- Información Laboral -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6">
              <h2 class="text-lg font-semibold mb-4 flex items-center">
                Información Laboral
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p class="text-sm font-medium text-gray-500">Lugar de Trabajo</p>
                  <p class="text-sm">{{ prospect.WorkName || 'No especificado' }}</p>
                </div>
              </div>
            </div>

            <!-- Comentarios -->
            <div v-if="prospect.Comments" class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6">
              <h2 class="text-lg font-semibold mb-4 flex items-center">
                Comentarios
              </h2>
              <p class="text-sm">{{ prospect.Comments }}</p>
            </div>

            <!-- Fechas -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6">
              <h2 class="text-lg font-semibold mb-4 flex items-center">
                Información de Fechas
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p class="text-sm font-medium text-gray-500">Fecha de Registro</p>
                  <p class="text-sm">{{ formatDateTime(prospect.DateStamp) }}</p>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-500">Último Seguimiento</p>
                  <p class="text-sm">{{ formatDate(prospect.LastFollow) }}</p>
                </div>
              </div>
            </div>

            <!-- Botones de acción -->
            <div class="flex flex-col sm:flex-row gap-3 justify-end mt-6">
              <button 
                @click="addFollowUp(prospect.ProspectId)" 
                class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Agregar seguimiento
              </button>
              <button 
                @click="verSeguimientos(prospect.ProspectId)" 
                class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                Ver seguimientos
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getProspects } from "@/services/api";

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      loading: false,
      errorMessage: null,
      prospectId: null,
      prospect: null,
      turnos: {
        15: 'Matutino',
        16: 'Vespertino',
        17: 'Nocturno',
        18: 'Fin de semana',
        19: 'Mixto'
        // Puedes agregar más turnos según sea necesario
      }
    };
  },
  async created() {
    // Capturar el ID del prospecto de la ruta
    this.prospectId = parseInt(this.$route.params.prospectId);
    
    await this.getUserFromBackend();
    if (this.user) {
      await this.loadProspectDetails();
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'No especificada';
      try {
        return new Date(dateString).toLocaleDateString('es-MX', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return dateString;
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return 'No especificada';
      try {
        return new Date(dateString).toLocaleString('es-MX', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    getTurnoName(shiftId) {
      return this.turnos[shiftId] || `Turno ${shiftId}`;
    },
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    async loadProspectDetails() {
      try {
        this.loading = true;
        this.errorMessage = null;
        
        let userName = '';
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          userName = this.user.email.match(/^a(\d+)@/)[1];
        }
        
        // Obtener todos los prospectos del usuario
        const response = await getProspects({
            _dc: new Date().getTime(),
            page: 1,
            start: 0,
            limit: 100, // Aumentamos el límite para asegurarnos de capturar el prospecto
            filter: JSON.stringify([
              {"property":"pSource","value":"GET_Prospects"},
              {"property":"pUserName","value": userName}
            ])
          });
        
        // Filtrar manualmente por ProspectId
        if (response.data && Array.isArray(response.data.prospects)) {
          const foundProspect = response.data.prospects.find(p => 
            parseInt(p.ProspectId) === this.prospectId
          );
          
          if (foundProspect) {
            this.prospect = foundProspect;
          } else {
            this.errorMessage = "No se encontró información para este prospecto";
          }
        } else {
          this.errorMessage = "Formato de respuesta inválido";
        }
      } catch (error) {
        console.error('Error al cargar detalles del prospecto:', error);
        this.errorMessage = 'Error al cargar los detalles del prospecto: ' + (error.response?.data?.message || error.message);
      } finally {
        this.loading = false;
      }
    },
    addFollowUp(prospectId) {
      this.$router.push(`/agregar-seguimiento/${prospectId}`);
    },
    verSeguimientos(prospectId) {
      this.$router.push(`/historial-seguimiento/${prospectId}`);
    },
    goBack() {
      this.$router.go(-1); // Volver a la página anterior
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');
        
        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>